import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileUp,
  Loader2,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import type { Client, ClientComplianceDocument } from '@core/types';
import {
  CLIENT_ENGAGEMENT_AGREEMENT_TEXT,
  REQUIRED_CLIENT_COMPLIANCE_DOC_TYPES,
} from '@core/constants/clientOnboarding';
import { clientComplianceService } from '@core/supabase/services/clientComplianceService';
import { clientService } from '@core/supabase/services/clientService';
import { isClientReadyForRecruitment } from '@core/clients/clientEngagementGate';
import { CandidateCollapsiblePanel } from '@modules/candidates/CandidateCollapsiblePanel';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';

type OrgUserLite = { orgId: string; name?: string | null };

export function ClientOnboardingPanel({
  client,
  user,
  onClientUpdated,
  compact = false,
}: {
  client: Client;
  user: OrgUserLite;
  onClientUpdated: (c: Client) => void;
  /** When true renders as collapsible right-rail panels instead of a full-page card */
  compact?: boolean;
}) {
  const [docs, setDocs] = useState<ClientComplianceDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [pendingNote, setPendingNote] = useState(client.compliance_pending_request_note ?? '');
  const [rejectNote, setRejectNote] = useState<Record<string, string>>({});
  const [uploadKey, setUploadKey] = useState<Record<string, string>>({});
  const [readyOfflineConfirmOpen, setReadyOfflineConfirmOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await clientComplianceService.listByClient(client.id);
      setDocs(list);
    } finally {
      setLoading(false);
    }
  }, [client.id]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => { setPendingNote(client.compliance_pending_request_note ?? ''); }, [client.compliance_pending_request_note]);

  const ready = isClientReadyForRecruitment(client);
  const approvedByType = new Set(docs.filter((d) => d.status === 'approved').map((d) => d.doc_type));

  const savePendingNote = async () => {
    setBusy('note');
    try {
      const updated = await clientService.update(client.id, {
        compliance_pending_request_note: pendingNote.trim() || null,
      });
      onClientUpdated(updated);
    } finally { setBusy(null); }
  };

  const onUpload = async (docType: string, file: File | null) => {
    if (!file || !user.orgId) return;
    setBusy(`up-${docType}`);
    try {
      await clientComplianceService.uploadDocument(user.orgId, client.id, docType, file, user.name ?? null);
      const nextClient = await clientComplianceService.syncClientReadiness(client.id, null);
      onClientUpdated(nextClient);
      setUploadKey((k) => ({ ...k, [docType]: crypto.randomUUID() }));
      await refresh();
    } finally { setBusy(null); }
  };

  const onReview = async (doc: ClientComplianceDocument, status: 'approved' | 'rejected') => {
    const note = rejectNote[doc.id]?.trim() || null;
    if (status === 'rejected' && !note) {
      setRejectNote((r) => ({ ...r, [doc.id]: r[doc.id] ?? '' }));
      return;
    }
    setBusy(doc.id);
    try {
      const { client: next } = await clientComplianceService.reviewAndSyncClient(
        doc.id, client.id, status, user.name ?? null, note
      );
      onClientUpdated(next);
      await refresh();
    } finally { setBusy(null); }
  };

  const runMarkReadyOffline = async () => {
    setBusy('override');
    try {
      const next = await clientComplianceService.markReadyOverride(client.id, user.name ?? null, pendingNote.trim() || null);
      onClientUpdated(next);
    } finally { setBusy(null); }
  };

  /* ── Shared inner content pieces ── */
  const requiredDocsContent = (
    <ul className="space-y-2">
      {REQUIRED_CLIENT_COMPLIANCE_DOC_TYPES.map(({ slug, label }) => {
        const hasApproved = approvedByType.has(slug);
        return (
          <li key={slug} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border vscode-border vscode-bg-secondary px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              {hasApproved
                ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />
                : <XCircle className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />}
              <span className="text-xs vscode-text-primary">{label}</span>
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs vscode-text-link cursor-pointer">
              <FileUp className="h-3.5 w-3.5" />
              {busy === `up-${slug}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Upload'}
              <input key={uploadKey[slug] ?? slug} type="file" className="sr-only"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" disabled={!!busy || !user.orgId}
                onChange={(e) => { const f = e.target.files?.[0]; void onUpload(slug, f ?? null); e.target.value = ''; }} />
            </label>
          </li>
        );
      })}
    </ul>
  );

  const uploadsContent = loading ? (
    <p className="text-xs vscode-text-tertiary py-3 flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</p>
  ) : docs.length === 0 ? (
    <p className="text-xs vscode-text-tertiary py-2">No files uploaded yet.</p>
  ) : (
    <ul className="space-y-2">
      {docs.map((d) => (
        <li key={d.id} className="rounded-lg border vscode-border vscode-bg-secondary p-2.5 text-xs">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="font-medium vscode-text-primary">{d.file_name}</p>
              <p className="vscode-text-tertiary">
                {REQUIRED_CLIENT_COMPLIANCE_DOC_TYPES.find((t) => t.slug === d.doc_type)?.label ?? d.doc_type}
                {' · '}{d.status.replace('_', ' ')}
                {d.uploaded_by ? ` · ${d.uploaded_by}` : ''}
              </p>
              {d.review_note && <p className="vscode-text-secondary mt-0.5">Note: {d.review_note}</p>}
            </div>
            <a href={clientComplianceService.getPublicUrl(d.storage_path)} target="_blank" rel="noreferrer"
              className="text-xs vscode-text-link shrink-0">Open</a>
          </div>
          {d.status === 'pending_review' && (
            <div className="mt-2 flex flex-wrap items-end gap-1.5">
              <input className="input flex-1 min-w-[120px] text-xs py-1"
                placeholder="Rejection reason" value={rejectNote[d.id] ?? ''}
                onChange={(e) => setRejectNote((r) => ({ ...r, [d.id]: e.target.value }))} />
              <button type="button" className="btn-secondary text-xs py-1" disabled={busy === d.id}
                onClick={() => void onReview(d, 'rejected')}>Reject</button>
              <button type="button" className="btn-primary text-xs py-1" disabled={busy === d.id}
                onClick={() => void onReview(d, 'approved')}>
                {busy === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Approve'}
              </button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  const pendingNoteContent = (
    <div className="space-y-2">
      <textarea className="input min-h-[64px] w-full text-xs resize-y"
        placeholder="e.g. Please upload GST certificate — previous scan unreadable."
        value={pendingNote} onChange={(e) => setPendingNote(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-secondary text-xs py-1" disabled={busy === 'note'}
          onClick={() => void savePendingNote()}>
          {busy === 'note' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save note'}
        </button>
        <button type="button"
          className="btn-secondary text-xs py-1 border-amber-600/40 text-amber-600 dark:text-amber-300 hover:bg-amber-500/10"
          disabled={busy === 'override' || ready} onClick={() => setReadyOfflineConfirmOpen(true)}>
          {busy === 'override' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Mark ready (offline)'}
        </button>
      </div>
    </div>
  );

  const engagementContent = (
    <>
      <pre className="text-xs vscode-text-tertiary whitespace-pre-wrap font-sans max-h-48 overflow-y-auto leading-relaxed">
        {CLIENT_ENGAGEMENT_AGREEMENT_TEXT}
      </pre>
      {client.client_consent_accepted_at ? (
        <p className="text-xs vscode-text-secondary mt-2">
          Accepted {formatDateTimeGmt(client.client_consent_accepted_at)}
          {client.client_consent_version ? ` · v${client.client_consent_version}` : ''}
        </p>
      ) : (
        <p className="text-xs text-amber-500 mt-2">No consent timestamp on record.</p>
      )}
    </>
  );

  /* ── Compact: collapsible right-rail panels ── */
  if (compact) {
    return (
      <>
      <ConfirmDialog
        open={readyOfflineConfirmOpen}
        onClose={() => setReadyOfflineConfirmOpen(false)}
        onConfirm={() => {
          setReadyOfflineConfirmOpen(false);
          void runMarkReadyOffline();
        }}
        title="Mark ready offline?"
        message="Mark this client ready to recruit without waiting for in-app document approvals? Use when originals were collected offline."
        confirmLabel="Mark ready"
        variant="primary"
      />
      <div className="space-y-2">
        {!ready && (
          <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2 text-xs text-amber-600 dark:text-amber-300 flex gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-400 mt-0.5" />
            <p>Recruiting is blocked. Approve required documents or mark ready.</p>
          </div>
        )}
        <CandidateCollapsiblePanel storageKey={`cop-${client.id}-engagement`} title="Engagement declaration">
          {engagementContent}
        </CandidateCollapsiblePanel>
        <CandidateCollapsiblePanel storageKey={`cop-${client.id}-req-docs`} title={`Required documents (${approvedByType.size}/${REQUIRED_CLIENT_COMPLIANCE_DOC_TYPES.length} approved)`}>
          {requiredDocsContent}
        </CandidateCollapsiblePanel>
        <CandidateCollapsiblePanel storageKey={`cop-${client.id}-uploads`} title={`Document review (${docs.length} file${docs.length !== 1 ? 's' : ''})`}>
          {uploadsContent}
        </CandidateCollapsiblePanel>
        <CandidateCollapsiblePanel storageKey={`cop-${client.id}-pending`} title="Pending request / mark ready">
          {pendingNoteContent}
        </CandidateCollapsiblePanel>
        <p className="text-xs vscode-text-tertiary px-1">
          Offboarding via <Link to="/settings/offboarding" className="vscode-text-link">Settings → Offboarding</Link>.
        </p>
      </div>
      </>
    );
  }

  /* ── Full layout (used in /clients/new onboarding wizard) ── */
  return (
    <>
    <ConfirmDialog
      open={readyOfflineConfirmOpen}
      onClose={() => setReadyOfflineConfirmOpen(false)}
      onConfirm={() => {
        setReadyOfflineConfirmOpen(false);
        void runMarkReadyOffline();
      }}
      title="Mark ready offline?"
      message="Mark this client ready to recruit without waiting for in-app document approvals? Use when originals were collected offline."
      confirmLabel="Mark ready"
      variant="primary"
    />
    <section className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b vscode-border pb-4">
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wider vscode-text-secondary flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-500/90" />
            Document uploads &amp; review
          </h2>
          <p className="text-sm vscode-text-tertiary mt-1 max-w-2xl">
            Upload and approve required documents. Recruiting is unblocked only after all required documents are approved.
          </p>
        </div>
        {ready ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/35 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-300">
            <CheckCircle2 className="h-3.5 w-3.5" /> Ready to recruit
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/35 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-200">
            <Clock className="h-3.5 w-3.5" /> Onboarding
          </span>
        )}
      </div>
      {!ready && (
        <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 px-4 py-3 text-sm text-amber-100/90 flex gap-2">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-400" />
          <div>
            <p className="font-medium text-amber-100">Requisitions and new jobs are blocked</p>
            <p className="text-amber-100/80 text-xs mt-1">
              Approve one uploaded file for each required document type below, or use &quot;Mark ready&quot; if originals were verified outside the app.
            </p>
          </div>
        </div>
      )}
      <details className="rounded-lg border vscode-border vscode-bg-secondary">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium vscode-text-secondary hover:vscode-text-primary">
          Engagement disclaimer text (shown at /clients/new)
        </summary>
        <div className="border-t vscode-border px-4 py-3">{engagementContent}</div>
      </details>
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary mb-3">Required documents</h3>
        {requiredDocsContent}
      </div>
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary mb-2">Uploads &amp; review</h3>
        {uploadsContent}
      </div>
      <div className="border-t vscode-border pt-4 space-y-2">
        <label className="label vscode-text-secondary">Pending request to client (missing items, follow-up)</label>
        {pendingNoteContent}
      </div>
      <p className="text-xs vscode-text-tertiary">
        Offboarding the hiring company is handled by archiving the client record and closing jobs; tenant-level exit is under{' '}
        <Link to="/settings/offboarding" className="vscode-text-link">Settings → Offboarding</Link>.
      </p>
    </section>
    </>
  );
}
